import React from 'react';

// Helper function for date formatting
const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

// Enhanced status badge with better styling
const StatusBadge = ({ status }) => {
    const statusConfig = {
        approved: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: '✓' },
        approved_by_director: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: '✓' },
        rejected: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: '✗' },
        rejected_by_director: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: '✗' },
        pending: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: '⏳' },
        pending_director_approval: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: '⏳' },
        submitted: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', icon: '📋' },
        processing_by_procurement: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', icon: '⚙️' },
        completed: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', icon: '🎉' },
        cancelled: { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200', icon: '❌' },
        on_hold: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', icon: '⏸️' },
        draft: { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200', icon: '📝' },
    };
    
    const config = statusConfig[status] || statusConfig.draft;
    const displayText = status ? status.replace(/_/g, ' ').toUpperCase() : 'UNKNOWN';
    
    return (
        <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border ${config.bg} ${config.text} ${config.border}`}>
            <span>{config.icon}</span>
            {displayText}
        </div>
    );
};

// Enhanced action button
const ActionButton = ({ onClick, label, variant = 'primary', icon, disabled = false }) => {
    const variants = {
        primary: 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm',
        success: 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm',
        danger: 'bg-red-600 hover:bg-red-700 text-white shadow-sm',
        warning: 'bg-amber-600 hover:bg-amber-700 text-white shadow-sm',
        info: 'bg-sky-600 hover:bg-sky-700 text-white shadow-sm'
    };
    
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${variants[variant]} disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105`}
        >
            {icon && <span className="text-base">{icon}</span>}
            {label}
        </button>
    );
};

// Info item component
const InfoItem = ({ label, value, icon }) => (
    <div className="flex items-center gap-2 text-sm">
        {icon && <span className="text-gray-400 text-base">{icon}</span>}
        <span className="text-gray-600">{label}:</span>
        <span className="text-gray-900 font-medium">{value}</span>
    </div>
);

// Priority badge
const PriorityBadge = ({ priority }) => {
    const config = {
        urgent: { bg: 'bg-red-100', text: 'text-red-800', icon: '🔥' },
        high: { bg: 'bg-orange-100', text: 'text-orange-800', icon: '⚡' },
        medium: { bg: 'bg-blue-100', text: 'text-blue-800', icon: '📌' },
        low: { bg: 'bg-gray-100', text: 'text-gray-800', icon: '📎' }
    };
    
    const priorityLevel = priority || 'medium';
    const style = config[priorityLevel] || config.medium;
    
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${style.bg} ${style.text}`}>
            <span>{style.icon}</span>
            {priorityLevel.toUpperCase()}
        </span>
    );
};

// Enhanced section component
const InfoSection = ({ title, children, variant = 'default' }) => {
    const variants = {
        default: 'bg-gray-50 border-gray-200',
        success: 'bg-emerald-50 border-emerald-200',
        warning: 'bg-amber-50 border-amber-200',
        info: 'bg-blue-50 border-blue-200',
        danger: 'bg-red-50 border-red-200'
    };
    
    return (
        <div className={`p-4 rounded-lg border ${variants[variant]}`}>
            <h4 className="font-semibold text-gray-900 mb-2">{title}</h4>
            <div className="space-y-2">
                {children}
            </div>
        </div>
    );
};

const TravelRequestCard = ({ request, onApprove, onReject, onAddItinerary, onReviewReport }) => {
    // Extract and process data (keeping original logic)
    const {
        _id,
        title,
        description,
        currentStatus,
        status,
        priority,
        dueDate,
        createdBy,
        projectId,
        totalEstimatedCost,
        items = [],
        directorApproval,
        itineraries,
        missionReports,
        destination,
        startDate,
        endDate,
        purpose,
        transportMode,
        accommodationType,
        estimatedCost
    } = request || {};

    const actualStatus = currentStatus || status || 'unknown';
    const isApproved = actualStatus === 'approved' || actualStatus === 'approved_by_director';
    const isRejected = actualStatus === 'rejected' || actualStatus === 'rejected_by_director';
    const isPending = actualStatus === 'pending' || actualStatus === 'pending_director_approval' || actualStatus === 'submitted';
    
    // More robust check for itineraries
    const hasItinerary = Array.isArray(itineraries) && itineraries.length > 0;
    const hasMissionReport = Array.isArray(missionReports) && missionReports.length > 0;
    const isReportReviewed = hasMissionReport && missionReports[0]?.status === 'reviewed';

    const travelPurpose = purpose || title || (items.length > 0 ? items[0].description : 'Travel Request');
    const travelDestination = destination || 'N/A';
    const staffName = createdBy ? `${createdBy.firstName || ''} ${createdBy.lastName || ''}`.trim() : 'N/A';
    const projectName = projectId ? (projectId.projectTitle || projectId.name) : 'N/A';
    const budget = estimatedCost || totalEstimatedCost || 
        (items.length > 0 ? items.reduce((sum, item) => sum + (item.estimatedCost || 0), 0) : 0);

    const getActionButtons = () => {
        const buttons = [];
        
        if (isPending) {
            buttons.push(
                <ActionButton 
                    key="approve"
                    onClick={() => onApprove(_id)} 
                    label="Approve" 
                    variant="success"
                    icon="✓"
                />,
                <ActionButton 
                    key="reject"
                    onClick={() => onReject(_id)} 
                    label="Reject" 
                    variant="danger"
                    icon="✗"
                />
            );
        }
        
        // Show Add Itinerary button for approved requests without itinerary
        if (isApproved && !hasItinerary) {
            buttons.push(
                <ActionButton 
                    key="itinerary"
                    onClick={() => onAddItinerary(_id)} 
                    label="Add Itinerary" 
                    variant="warning"
                    icon="✈️"
                />
            );
        }
        
        // Also show Add Itinerary button for approved requests that have itinerary but might need updates
        if (isApproved && hasItinerary) {
            buttons.push(
                <ActionButton 
                    key="update-itinerary"
                    onClick={() => onAddItinerary(_id)} 
                    label="Update Itinerary" 
                    variant="info"
                    icon="📝"
                />
            );
        }
        
        // Only show review report button if we have reports and they're not reviewed
        if (hasMissionReport && !isReportReviewed) {
            buttons.push(
                <ActionButton 
                    key="review"
                    onClick={() => onReviewReport(missionReports[0]?._id)} 
                    label="Review Report" 
                    variant="info"
                    icon="📋"
                />
            );
        }
        
        return buttons;
    };

    // Debug info - remove in production
    const debugInfo = process.env.NODE_ENV === 'development' ? (
        <div className="text-xs text-gray-500 mt-2 p-2 bg-gray-50 rounded">
            Debug: Status={actualStatus}, Approved={isApproved}, HasItinerary={hasItinerary}, Pending={isPending}
        </div>
    ) : null;

    return (
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-100">
                <div className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900 mb-1">{travelPurpose}</h3>
                        <div className="flex items-center gap-3">
                            <InfoItem label="Staff" value={staffName} icon="👤" />
                            <PriorityBadge priority={priority} />
                        </div>
                    </div>
                    <StatusBadge status={actualStatus} />
                </div>
            </div>

            {/* Main Content */}
            <div className="p-6 space-y-6">
                {/* Key Travel Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                        <InfoItem label="Destination" value={travelDestination} icon="📍" />
                        <InfoItem label="Project" value={projectName} icon="📊" />
                        {startDate && <InfoItem label="Start Date" value={formatDate(startDate)} icon="📅" />}
                        {transportMode && <InfoItem label="Transport" value={transportMode} icon="🚗" />}
                    </div>
                    <div className="space-y-3">
                        <InfoItem label="Budget" value={budget > 0 ? `$${budget.toFixed(2)}` : 'N/A'} icon="💰" />
                        <InfoItem label="Due Date" value={formatDate(dueDate)} icon="⏰" />
                        {endDate && <InfoItem label="End Date" value={formatDate(endDate)} icon="📅" />}
                        {accommodationType && <InfoItem label="Accommodation" value={accommodationType} icon="🏨" />}
                    </div>
                </div>

                {/* Description */}
                {description && description !== title && (
                    <InfoSection title="Description">
                        <p className="text-gray-700 text-sm leading-relaxed">{description}</p>
                    </InfoSection>
                )}

                {/* Items List */}
                {items.length > 0 && (
                    <InfoSection title="Request Items">
                        <div className="space-y-2">
                            {items.map((item, index) => (
                                <div key={index} className="flex justify-between items-center py-2 px-3 bg-white rounded-lg border border-gray-100">
                                    <div>
                                        <span className="text-sm font-medium text-gray-900">{item.description}</span>
                                        {item.quantity && <span className="text-xs text-gray-500 ml-2">Qty: {item.quantity}</span>}
                                    </div>
                                    {item.estimatedCost && (
                                        <span className="text-sm font-semibold text-gray-900">${item.estimatedCost.toFixed(2)}</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </InfoSection>
                )}

                {/* Status-specific sections */}
                {directorApproval && (
                    <InfoSection 
                        title="Director Approval" 
                        variant={isApproved ? "success" : isRejected ? "danger" : "info"}
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <InfoItem label="Status" value={directorApproval.status || 'N/A'} />
                            {directorApproval.approvedBy && (
                                <InfoItem 
                                    label="Approved By" 
                                    value={`${directorApproval.approvedBy.firstName || ''} ${directorApproval.approvedBy.lastName || ''}`.trim()} 
                                />
                            )}
                            {directorApproval.approvedAt && (
                                <InfoItem label="Date" value={formatDate(directorApproval.approvedAt)} />
                            )}
                        </div>
                        {directorApproval.comments && (
                            <div className="mt-3 p-3 bg-white bg-opacity-50 rounded-lg">
                                <p className="text-sm text-gray-700">
                                    <span className="font-medium">Comments:</span> {directorApproval.comments}
                                </p>
                            </div>
                        )}
                    </InfoSection>
                )}

                {hasItinerary && (
                    <InfoSection title="Travel Itinerary" variant="info">
                        {itineraries.map((itinerary, index) => (
                            <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                {itinerary.flight && <InfoItem label="Flight" value={itinerary.flight} icon="✈️" />}
                                {itinerary.hotel && <InfoItem label="Hotel" value={itinerary.hotel} icon="🏨" />}
                                {itinerary.transport && <InfoItem label="Transport" value={itinerary.transport} icon="🚗" />}
                            </div>
                        ))}
                    </InfoSection>
                )}

                {hasMissionReport && (
                    <InfoSection 
                        title={`Mission Report ${isReportReviewed ? '(Reviewed)' : '(Pending Review)'}`}
                        variant={isReportReviewed ? "success" : "warning"}
                    >
                        <p className="text-sm text-gray-700">
                            {isReportReviewed 
                                ? `Review Comments: ${missionReports[0]?.comments || 'No additional comments'}` 
                                : 'Report has been submitted and is awaiting review.'}
                        </p>
                    </InfoSection>
                )}

                {/* Debug info */}
                {debugInfo}
            </div>

            {/* Action Buttons */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                <div className="flex flex-wrap gap-3">
                    {getActionButtons().length > 0 ? (
                        getActionButtons()
                    ) : (
                        <p className="text-sm text-gray-500 italic">No actions available for this request</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TravelRequestCard;